/**
 * <tf-sidebar v2> — Web Component completo che sostituisce sidebar legacy.
 *
 * Sprint 10 — Refactor strutturale (post Sprint 9.5 rollback).
 *
 * Strategia:
 *   - Opt-in via `<body data-tf-sidebar-v2="1">` su pagine specifiche
 *   - Su pagine v2: legacy ap-core.initSidebar / applyMenu skip via guard
 *   - Web Component renderizza markup IDENTICO al legacy (stessi ID + class)
 *     → tf-soft-nav.setActive continua a funzionare (cerca .ap-nav-link[data-page])
 *   - Listen window.tfAuth state changes → re-render solo su user data change
 *   - Listen `tf:navigated` event → aggiorna SOLO active link (no rebuild)
 *
 * Rollback path:
 *   - Rimuovi `data-tf-sidebar-v2` dal body → legacy reattiva
 *   - Niente file legacy modificati (eccetto guard 1-line in ap-core/app-sidebar)
 *
 * @since v9.16 (Sprint 10 — sidebar refactor)
 */

import * as auth from '../state/auth';

// ============================================================
// Types & Config
// ============================================================

interface MenuItem {
  name: string;
  route: string;
  page: string;
  icon: string;
  plan: string;
  shortcut?: string;
  feature?: string;
  badge?: string;
}

interface MenuSection {
  id: string;
  title: string;
  items: MenuItem[];
}

interface TfMenuConfig {
  sections: MenuSection[];
}

declare global {
  interface Window {
    TF_MENU_CONFIG?: TfMenuConfig;
    /** Set quando v2 prende ownership della sidebar */
    _tfSidebarV2Active?: boolean;
    /** API esposta del WC */
    tfSidebarV2?: {
      version: string;
      refresh: () => void;
    };
  }
}

const PLAN_RANK: Record<string, number> = {
  free: 0,
  starter: 1,
  professional: 2,
  business: 3,
  business_plus: 4,
  trial: 99, // trial vede tutto
  expired: -1,
};

const PLAN_LABEL: Record<string, string> = {
  trial: 'Prova Gratuita',
  starter: 'Starter',
  professional: 'Professional',
  business: 'Business',
  business_plus: 'Business Plus',
  expired: 'Scaduto',
  free: 'Free',
};

// SVG icons map (subset coerente con menu config)
const ICON_PATHS: Record<string, string> = {
  dashboard: 'M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z',
  fatture: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8',
  ncredito: 'M12 22a10 10 0 1 1 0-20 10 10 0 0 1 0 20zM12 6v6 M12 16h.01',
  ddt: 'M3 7v13h18V7H3zM3 7l9-5 9 5 M9 11h6 M9 15h6',
  preventivi: 'M14 2v6h6 M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8 M16 13H8 M16 17H8',
  clienti: 'M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M22 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75',
  fornitori: 'M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z M3.27 6.96L12 12.01l8.73-5.05 M12 22.08V12',
  prodotti: 'M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z M7 7h.01',
  magazzino: 'M21 8v13H3V8 M1 3h22v5H1z M10 12h4',
  adempimenti: 'M9 11l3 3 3-3 M12 14V5 M5 19h14',
  report: 'M3 3v18h18 M7 12l4-4 4 4 5-5',
  conservazione: 'M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z',
  commercialista: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z',
  team: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M23 21v-2a4 4 0 0 0-3-3.87',
  ticket: 'M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z',
  impostazioni: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z',
  abbonamento: 'M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4 M4 6v12c0 1.1.9 2 2 2h14v-4 M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4z',
};

function defaultIconPath(): string {
  return 'M12 2L2 7l10 5 10-5-10-5z M2 17l10 5 10-5 M2 12l10 5 10-5';
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] || c
  );
}

// ============================================================
// Component
// ============================================================

export class TfSidebarV2Element extends HTMLElement {
  private _user: ReturnType<typeof auth.getUser> = null;
  private _unsubscribeAuth?: () => void;
  private _navHandler?: (e: Event) => void;
  private _renderedSignature: string = '';

  connectedCallback(): void {
    // Mark global so legacy code può fare guard
    if (typeof window !== 'undefined') {
      window._tfSidebarV2Active = true;
    }

    this._user = auth.getUser();

    // Render iniziale (skeleton se no user / no menu config)
    this._render();

    // Subscribe auth changes
    this._unsubscribeAuth = auth.subscribe((state) => {
      const newUser = state.user;
      if (this._user !== newUser) {
        this._user = newUser;
        this._render();
      }
    });

    // Listen navigation events
    this._navHandler = () => this._updateActiveLink();
    document.addEventListener('tf:navigated', this._navHandler);

    // Poll for TF_MENU_CONFIG if not yet ready (script ordering can vary).
    // Bail out after 30 attempts (3s) — config is global, should be there soon.
    if (typeof window !== 'undefined' && !window.TF_MENU_CONFIG) {
      let attempts = 0;
      const check = () => {
        attempts++;
        if (window.TF_MENU_CONFIG) {
          this._render();  // signature includes cfg-ready, so this triggers
          this._updateActiveLink();
          return;
        }
        if (attempts < 30) setTimeout(check, 100);
      };
      setTimeout(check, 50);
    }

    // Update active link al primo render
    setTimeout(() => this._updateActiveLink(), 0);
  }

  disconnectedCallback(): void {
    if (this._unsubscribeAuth) this._unsubscribeAuth();
    if (this._navHandler) document.removeEventListener('tf:navigated', this._navHandler);
    if (typeof window !== 'undefined') {
      window._tfSidebarV2Active = false;
    }
  }

  /**
   * Public API: forza re-render (es. cambio piano dopo upgrade).
   */
  refresh(): void {
    this._user = auth.getUser();
    this._render();
    this._updateActiveLink();
  }

  // ============================================================
  // Render
  // ============================================================

  private _render(): void {
    // Idempotency: se signature uguale, skip (no DOM mutation)
    const sig = this._computeSignature();
    if (sig === this._renderedSignature) return;
    this._renderedSignature = sig;

    const cfg = (typeof window !== 'undefined' && window.TF_MENU_CONFIG) || { sections: [] };
    const user = this._user;
    const userPiano = (user as { piano_effettivo?: string; piano?: string } | null)?.piano_effettivo
      || (user as { piano?: string } | null)?.piano
      || 'free';
    const userRank = PLAN_RANK[userPiano] ?? 0;

    const fullName = user ? `${(user.nome || '').trim()} ${(user.cognome || '').trim()}`.trim() || 'Utente' : 'Caricamento...';
    const initials = fullName
      .split(/\s+/)
      .filter(Boolean)
      .map((p) => p.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('') || '--';
    const planLabel = PLAN_LABEL[userPiano] || userPiano.replace('_', ' ');

    const navHtml = cfg.sections
      .map((section) => this._renderSection(section, userRank))
      .filter(Boolean)
      .join('');

    this.innerHTML = `
      <aside id="ap-sidebar" class="fixed left-0 top-0 z-50 hidden w-[272px] flex-col bg-[#0f172a] transition-transform duration-300 lg:flex lg:translate-x-0" style="height:100dvh;height:100vh;-webkit-transform:translateX(-100%);transform:translateX(-100%)">
        <!-- Brand logo area -->
        <div class="flex h-[72px] items-center gap-3 px-6" id="sidebar-logo-area">
          <div id="sidebar-logo-default" class="tf-brand-logo">
            <svg class="tf-brand-logo-svg" viewBox="0 0 44 44" aria-label="TurboFattura">
              <defs>
                <linearGradient id="tf-bg-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#fcd34d"/>
                  <stop offset="50%" stop-color="#f59e0b"/>
                  <stop offset="100%" stop-color="#b45309"/>
                </linearGradient>
              </defs>
              <rect width="44" height="44" rx="11" fill="url(#tf-bg-grad)"/>
              <path d="M24.75 8.8L14.3 24.2h7.15l-2.2 11L29.7 19.8h-7.15l2.2-11z" fill="#fff" stroke="#ffffff" stroke-width="1.4" stroke-linejoin="round"/>
            </svg>
          </div>
          <span id="sidebar-brand-text" class="text-lg font-bold text-white">TurboFattura</span>
          <button id="ap-sidebar-close" aria-label="Chiudi menu" class="ml-auto rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-white/10 hover:text-white lg:hidden">
            <svg class="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
          </button>
        </div>

        <!-- Nav menu -->
        <nav class="flex-1 overflow-y-auto px-3 pt-6 pb-3" data-tf-sidebar-v2-rendered="1">
          ${navHtml}
        </nav>

        <!-- User card + invoice counter -->
        ${this._renderUserCard(fullName, initials, planLabel)}
      </aside>
    `;

    // Wire logout
    const logoutBtn = this.querySelector<HTMLButtonElement>('#logout-btn');
    if (logoutBtn) {
      logoutBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const w = window as Window & { AP?: { logout?: () => void } };
        if (w.AP?.logout) w.AP.logout();
        else {
          auth.clearAuth();
          window.location.href = '/login';
        }
      });
    }

    // Wire close mobile
    const closeBtn = this.querySelector<HTMLButtonElement>('#ap-sidebar-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        const sb = this.querySelector('#ap-sidebar');
        if (sb) sb.classList.remove('translate-x-0');
      });
    }
  }

  private _computeSignature(): string {
    const u = this._user;
    const cfgReady = typeof window !== 'undefined' && !!window.TF_MENU_CONFIG?.sections?.length;
    if (!u) return `no-user|cfg-${cfgReady}`;
    const piano = (u as { piano_effettivo?: string; piano?: string } | null)?.piano_effettivo
      || (u as { piano?: string } | null)?.piano
      || 'free';
    return `${u.id}|${u.email}|${u.nome}|${u.cognome}|${piano}|${u.ruolo}|cfg-${cfgReady}`;
  }

  private _renderSection(section: MenuSection, userRank: number): string {
    const itemsHtml = section.items
      .filter((item) => {
        const requiredRank = PLAN_RANK[item.plan] ?? 0;
        return userRank >= requiredRank || item.plan === 'free';
      })
      .map((item) => this._renderItem(item, userRank))
      .join('');

    if (!itemsHtml.trim()) return '';

    return `
      <div class="mb-4">
        <p class="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">${escapeHtml(section.title)}</p>
        ${itemsHtml}
      </div>
    `;
  }

  private _renderItem(item: MenuItem, userRank: number): string {
    const requiredRank = PLAN_RANK[item.plan] ?? 0;
    const locked = userRank < requiredRank && item.plan !== 'free';
    const iconPath = ICON_PATHS[item.icon] || defaultIconPath();
    const lockedClasses = locked ? 'opacity-50 cursor-not-allowed pointer-events-none' : '';

    return `
      <a href="${escapeHtml(item.route)}" data-page="${escapeHtml(item.page)}" class="ap-nav-link group relative flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium transition-all duration-150 hover:bg-white/[0.04] hover:text-white text-slate-400 ${lockedClasses}">
        <svg class="size-[18px] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.6">
          <path stroke-linecap="round" stroke-linejoin="round" d="${iconPath}"/>
        </svg>
        <span class="flex-1 truncate">${escapeHtml(item.name)}</span>
        ${locked ? '<svg class="size-3.5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2zm10-10V7a4 4 0 0 0-8 0v4h8z"/></svg>' : ''}
      </a>
    `;
  }

  private _renderUserCard(fullName: string, initials: string, planLabel: string): string {
    return `
      <div class="border-t border-white/5 px-3 py-3">
        <div class="flex items-center gap-3 px-2 py-2">
          <div id="sidebar-initials" class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-amber-500 to-amber-600 text-xs font-bold text-white">${escapeHtml(initials)}</div>
          <div class="min-w-0 flex-1">
            <p id="sidebar-name" class="truncate text-sm font-semibold text-slate-200">${escapeHtml(fullName)}</p>
            <p id="sidebar-plan" class="truncate text-[11px] text-slate-500">${escapeHtml(planLabel)}</p>
          </div>
          <button id="logout-btn" class="rounded-lg p-1.5 text-secondary/40 transition-all hover:bg-red-50/10 hover:text-red-400 hover:scale-110" title="Esci" aria-label="Esci">
            <svg class="size-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.6"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15"/><path d="m18 15 3-3m0 0-3-3m3 3H9"/></svg>
          </button>
        </div>
      </div>
    `;
  }

  private _updateActiveLink(): void {
    const path = window.location.pathname.split('/').pop()?.replace('.html', '') || 'dashboard';
    const activeMap: Record<string, string> = {
      dashboard: 'dashboard',
      'fatture-sdi': 'fatture',
      clienti: 'clienti',
      'note-credito': 'note-credito',
      prodotti: 'prodotti',
      report: 'report',
      conservazione: 'conservazione',
      'invio-commercialista': 'commercialista',
      impostazioni: 'impostazioni',
      ticket: 'ticket',
      abbonamento: 'abbonamento',
      'gestione-team': 'team',
      ddt: 'ddt',
      preventivi: 'preventivi',
      fornitori: 'fornitori',
      magazzino: 'magazzino',
      adempimenti: 'adempimenti',
      'nuova-fattura': 'fatture',
    };
    const activePage = activeMap[path] || path;

    // Rimuovi active da tutti
    this.querySelectorAll<HTMLAnchorElement>('.ap-nav-link').forEach((link) => {
      link.classList.remove('bg-white/[0.08]', 'text-white');
      link.classList.add('text-slate-400');
      link.removeAttribute('aria-current');
    });

    // Aggiungi active a current page
    const activeLink = this.querySelector<HTMLAnchorElement>(`.ap-nav-link[data-page="${activePage}"]`);
    if (activeLink) {
      activeLink.classList.remove('text-slate-400');
      activeLink.classList.add('bg-white/[0.08]', 'text-white');
      activeLink.setAttribute('aria-current', 'page');
    }
  }
}

// Auto-register
if (typeof customElements !== 'undefined' && !customElements.get('tf-sidebar-v2')) {
  customElements.define('tf-sidebar-v2', TfSidebarV2Element);
}

// Esponi API
if (typeof window !== 'undefined') {
  window.tfSidebarV2 = {
    version: '1.0.0',
    refresh: () => {
      const el = document.querySelector<TfSidebarV2Element>('tf-sidebar-v2');
      if (el) el.refresh();
    },
  };
}
